/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import {
  CSSProperties,
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from "react";
import { LinearProgress } from "@mui/material";
import { PALETTE } from "styles/base/variables";
import { convertMobileNo } from "utils/converter";
import {
  OTHER_TAX_TYPE_LABELS,
  OTHER_TAX_TYPES,
  OtherTaxType
} from "models/paymentNotice.model";

// 급여관리 매트릭스(PayrollGrid)와 같은 색·치수를 쓴다.
const C = {
  good: PALETTE.progressCompleted,
  warn: "#F5A623",
  ink: PALETTE.text,
  ink2: "#52514E",
  muted: PALETTE.textMid,
  grid: PALETTE.borderLight,
  rule: PALETTE.border,
  headBg: "#FCFCFB",
  rowHi: PALETTE.primaryHover
};

const ROW_H = 34;
const CELL_W = 64;
const PHONE_W = 108;
const W = { flag: 5, chk: 34, name: 288, owner: 84 };
type LeftOffsets = { flag: number; chk: number; name: number; owner: number };
const leftOffsets = (selectable: boolean): LeftOffsets => {
  const chk = selectable ? W.chk : 0;
  return {
    flag: 0,
    chk: W.flag,
    name: W.flag + chk,
    owner: W.flag + chk + W.name
  };
};

const fixedWidth = (width: number): CSSProperties => ({
  width,
  minWidth: width,
  maxWidth: width,
  boxSizing: "border-box"
});
const pinBody = (
  leftPx: number,
  width: number,
  extra?: CSSProperties
): CSSProperties => ({
  position: "sticky",
  left: leftPx,
  zIndex: 2,
  background: PALETTE.light,
  ...fixedWidth(width),
  ...extra
});
const pinHead = (
  leftPx: number,
  width: number,
  top: number,
  extra?: CSSProperties
): CSSProperties => ({
  position: "sticky",
  left: leftPx,
  top,
  zIndex: 6,
  background: C.headBg,
  ...fixedWidth(width),
  ...extra
});

// 거래처 1곳의 부가세/종소세/법인세 납부서 현황 (§2.10 응답 3건을 거래처 기준으로 합친 것)
export interface OtherTaxRow {
  businessId: string;
  name: string;
  regNumber: string;
  incharge: string | null;
  kakaoMobileNo: string | null;
  userMobileNo: string | null;
  // 유형별 매칭된 납부서 건수
  counts: Record<OtherTaxType, number>;
  // 유형별 가장 최근 알림톡 발송 일시 (없으면 null)
  sentAt: Record<OtherTaxType, string | null>;
}

// 납부서가 올라왔는데 아직 그 유형 알림톡을 보내지 않음
export const needsSend = (row: OtherTaxRow, type: OtherTaxType) =>
  row.counts[type] > 0 && !row.sentAt[type];

export interface OtherTaxSortState {
  key: string; // "name" | "incharge" | "notice:<type>" | "sent:<type>"
  dir: 1 | -1;
}

interface OtherTaxGridProps {
  rows: OtherTaxRow[];
  loading?: boolean;
  sort: OtherTaxSortState;
  onSort: (key: string) => void;
  // 알림톡 발송 선택 모드에서만 체크 컬럼을 노출하고, 이때 셀 클릭 동작은 모두 막힌다.
  selectable?: boolean;
  // 발송 선택 모드의 대상 유형 — 해당 유형 칸을 강조한다.
  highlightType?: OtherTaxType | null;
  selected: Set<string>;
  onToggleSelected: (businessId: string) => void;
  onToggleAll: (checked: boolean) => void;
  onSelectRange: (businessIds: string[], checked: boolean) => void;
  // 납부서 칸 클릭 → 그 거래처·유형의 납부서 목록 팝업
  onNoticeCellClick: (row: OtherTaxRow, type: OtherTaxType) => void;
  // 수신 번호 칸 클릭 → 알림톡 수신 번호 변경 팝업
  onPhoneClick: (row: OtherTaxRow) => void;
  // 발송 완료 칸 클릭 → 발송한 알림톡 내용 팝업
  onDispatchClick: (row: OtherTaxRow, type: OtherTaxType) => void;
}

function Dot({ filled, color }: { filled: boolean; color: string }) {
  return (
    <span
      css={css`
        display: inline-block;
        width: 11px;
        height: 11px;
        border-radius: 3px;
        ${filled
          ? `background:${color};`
          : `box-shadow: inset 0 0 0 2px ${color};`}
      `}
    />
  );
}

const Dash = () => (
  <span
    css={css`
      color: ${C.muted};
      opacity: 0.5;
      font-size: 12px;
    `}
  >
    –
  </span>
);

const clickableCss = css`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  padding: 3px 5px;
  &:hover {
    outline: 2px solid ${PALETTE.primary}55;
    border-radius: 4px;
  }
`;

const fmtDateTime = (v: string) => v.replace("T", " ").slice(0, 16);

const GridRow = memo(function GridRow({
  row,
  index,
  selectable,
  highlightType,
  left,
  isSelected,
  onToggleSelected,
  onDragStart,
  onDragEnter,
  onNoticeCellClick,
  onPhoneClick,
  onDispatchClick
}: {
  row: OtherTaxRow;
  index: number;
  selectable: boolean;
  highlightType: OtherTaxType | null;
  left: LeftOffsets;
  isSelected: boolean;
  onToggleSelected: (id: string) => void;
  onDragStart: (index: number) => void;
  onDragEnter: (index: number) => void;
  onNoticeCellClick: (row: OtherTaxRow, type: OtherTaxType) => void;
  onPhoneClick: (row: OtherTaxRow) => void;
  onDispatchClick: (row: OtherTaxRow, type: OtherTaxType) => void;
}) {
  const pending = OTHER_TAX_TYPES.some((t) => needsSend(row, t));
  const hasAny = OTHER_TAX_TYPES.some(
    (t) => row.counts[t] > 0 || !!row.sentAt[t]
  );
  const flagColor = pending ? C.warn : "transparent";

  const hiCss = (type: OtherTaxType) =>
    highlightType === type ? `background: ${PALETTE.primary}0d;` : "";

  const noticeCell = (type: OtherTaxType): ReactNode => {
    const count = row.counts[type];
    if (!count) return <Dash />;
    const node = (
      <>
        <Dot filled color={C.good} />
        {count > 1 && (
          <span
            css={css`
              font-size: 10px;
              color: ${C.muted};
            `}
          >
            {count}
          </span>
        )}
      </>
    );
    if (selectable)
      return (
        <span
          css={css`
            display: inline-flex;
            align-items: center;
            gap: 3px;
          `}
        >
          {node}
        </span>
      );
    return (
      <span
        role="button"
        onClick={() => onNoticeCellClick(row, type)}
        title={`${OTHER_TAX_TYPE_LABELS[type]} 납부서 ${count}건 — 목록 보기`}
        css={clickableCss}
      >
        {node}
      </span>
    );
  };

  const dispatchCell = (type: OtherTaxType): ReactNode => {
    const sentAt = row.sentAt[type];
    if (sentAt) {
      const dot = <Dot filled color={C.good} />;
      if (selectable)
        return <span title={`${fmtDateTime(sentAt)} 발송`}>{dot}</span>;
      return (
        <span
          role="button"
          onClick={() => onDispatchClick(row, type)}
          title={`${fmtDateTime(sentAt)} 발송 — 발송한 알림톡 내용 보기`}
          css={clickableCss}
        >
          {dot}
        </span>
      );
    }
    if (row.counts[type] > 0)
      return (
        <span title="미발송">
          <Dot filled={false} color={C.warn} />
        </span>
      );
    return <Dash />;
  };

  const phoneCell = () => {
    const raw = row.kakaoMobileNo || row.userMobileNo || "";
    const display = raw ? convertMobileNo(raw) : "번호 없음";
    return (
      <span
        role={selectable ? undefined : "button"}
        onClick={selectable ? undefined : () => onPhoneClick(row)}
        title={selectable ? undefined : "클릭하여 번호 변경"}
        css={css`
          font-size: 11px;
          color: ${raw ? C.ink : C.muted};
          ${!selectable &&
          `cursor: pointer;
           border-bottom: 1px dashed transparent;
           &:hover { border-bottom-color: ${C.muted}; }`}
        `}
      >
        {display}
      </span>
    );
  };

  return (
    <tr
      onMouseDown={
        selectable
          ? (e) => {
              if ((e.target as HTMLElement).closest("[data-checkbox-cell]"))
                return;
              e.preventDefault();
              onDragStart(index);
            }
          : undefined
      }
      onMouseEnter={selectable ? () => onDragEnter(index) : undefined}
      css={css`
        height: ${ROW_H}px;
        ${!hasAny && "opacity:.55;"}
        ${selectable &&
        `cursor: pointer; user-select: none;
         ${
           isSelected
             ? `td { background: ${PALETTE.primary}14 !important; }`
             : ""
         }`}
        &:hover {
          opacity: 1;
        }
        &:hover td {
          background: ${C.rowHi};
        }
        td {
          border-bottom: 1px solid ${C.grid};
          text-align: center;
          font-size: 12px;
          color: ${C.ink};
          background: ${PALETTE.light};
        }
      `}
    >
      <td style={pinBody(left.flag, W.flag, { padding: 0 })}>
        <span
          css={css`
            display: block;
            width: ${W.flag}px;
            height: ${ROW_H}px;
            background: ${flagColor};
          `}
        />
      </td>
      {selectable && (
        <td data-checkbox-cell style={pinBody(left.chk, W.chk)}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelected(row.businessId)}
          />
        </td>
      )}
      <td
        style={pinBody(left.name, W.name, {
          textAlign: "left",
          padding: "0 8px"
        })}
      >
        <span
          title={row.regNumber}
          css={css`
            display: block;
            font-weight: 600;
            font-size: 12.5px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          `}
        >
          {row.name}
        </span>
      </td>
      <td
        style={pinBody(left.owner, W.owner, {
          borderRight: `1px solid ${C.rule}`
        })}
      >
        <span
          css={css`
            font-size: 11px;
            color: ${PALETTE.textMid};
          `}
        >
          {row.incharge || "-"}
        </span>
      </td>

      {OTHER_TAX_TYPES.map((type, i) => (
        <td
          key={`n-${type}`}
          css={css`
            ${i === 0 && `border-left:1px solid ${C.grid};`}
            min-width: ${CELL_W}px;
            ${hiCss(type)}
          `}
        >
          {noticeCell(type)}
        </td>
      ))}
      <td
        css={css`
          border-left: 1px solid ${C.grid};
          min-width: ${PHONE_W}px;
        `}
      >
        {phoneCell()}
      </td>
      {OTHER_TAX_TYPES.map((type) => (
        <td
          key={`d-${type}`}
          css={css`
            min-width: ${CELL_W}px;
            ${hiCss(type)}
          `}
        >
          {dispatchCell(type)}
        </td>
      ))}
    </tr>
  );
});

function OtherTaxGrid({
  rows,
  loading,
  sort,
  onSort,
  selectable = false,
  highlightType = null,
  selected,
  onToggleSelected,
  onToggleAll,
  onSelectRange,
  onNoticeCellClick,
  onPhoneClick,
  onDispatchClick
}: OtherTaxGridProps) {
  const left = useMemo(() => leftOffsets(selectable), [selectable]);
  const allChecked =
    rows.length > 0 && rows.every((r) => selected.has(r.businessId));

  // ── 드래그 선택 (엑셀식, PayrollGrid와 동일) ────────────────
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const dragRef = useRef<{ start: number; checked: boolean } | null>(null);

  const applyDrag = useCallback(
    (toIndex: number) => {
      const d = dragRef.current;
      if (!d) return;
      const lo = Math.min(d.start, toIndex);
      const hi = Math.max(d.start, toIndex);
      onSelectRange(
        rowsRef.current.slice(lo, hi + 1).map((r) => r.businessId),
        d.checked
      );
    },
    [onSelectRange]
  );

  const onDragStart = useCallback(
    (index: number) => {
      const id = rowsRef.current[index]?.businessId;
      if (id === undefined) return;
      dragRef.current = { start: index, checked: !selectedRef.current.has(id) };
      applyDrag(index);
    },
    [applyDrag]
  );

  const onDragEnter = useCallback(
    (index: number) => {
      if (dragRef.current) applyDrag(index);
    },
    [applyDrag]
  );

  useEffect(() => {
    const end = () => (dragRef.current = null);
    window.addEventListener("mouseup", end);
    return () => window.removeEventListener("mouseup", end);
  }, []);

  const mark = (key: string) =>
    sort.key === key ? (sort.dir > 0 ? " ▲" : " ▼") : "";

  const sortableTh = (key: string, label: string, extra?: string) => (
    <th
      key={key}
      onClick={() => onSort(key)}
      css={css`
        cursor: pointer;
        min-width: ${CELL_W}px;
        ${extra || ""}
      `}
    >
      {label}
      {mark(key)}
    </th>
  );

  return (
    <div
      css={css`
        border: 1px solid ${C.rule};
        border-radius: 8px;
        overflow: auto;
        max-height: calc(100vh - 300px);
        position: relative;
        background: ${PALETTE.light};
        width: fit-content;
        max-width: 100%;
      `}
    >
      {loading && (
        <div
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            z-index: 8;
          `}
        >
          <LinearProgress
            sx={{
              backgroundColor: PALETTE.primaryBackground,
              "& .MuiLinearProgress-bar": { backgroundColor: PALETTE.primary }
            }}
          />
        </div>
      )}
      <table
        css={css`
          border-collapse: separate;
          border-spacing: 0;
          width: max-content;
          th,
          td {
            padding: 0;
            margin: 0;
          }
          thead th {
            position: sticky;
            background: ${C.headBg};
            font-weight: 700;
            color: ${C.ink2};
            font-size: 11.5px;
            white-space: nowrap;
            border-bottom: 1px solid ${C.rule};
            z-index: 4;
          }
        `}
      >
        <thead>
          <tr
            css={css`
              th {
                top: 0;
                height: 40px;
              }
            `}
          >
            <th style={pinHead(left.flag, W.flag, 0, { padding: 0 })} />
            {selectable && (
              <th style={pinHead(left.chk, W.chk, 0)}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => onToggleAll(e.target.checked)}
                />
              </th>
            )}
            <th
              style={pinHead(left.name, W.name, 0, {
                textAlign: "left",
                padding: "0 8px",
                cursor: "pointer"
              })}
              onClick={() => onSort("name")}
            >
              거래처{mark("name")}
            </th>
            <th
              style={pinHead(left.owner, W.owner, 0, {
                borderRight: `1px solid ${C.rule}`,
                cursor: "pointer"
              })}
              onClick={() => onSort("incharge")}
            >
              담당자{mark("incharge")}
            </th>
            <th
              colSpan={OTHER_TAX_TYPES.length}
              css={css`
                border-left: 1px solid ${C.grid};
              `}
            >
              납부서
            </th>
            <th
              colSpan={OTHER_TAX_TYPES.length + 1}
              css={css`
                border-left: 1px solid ${C.grid};
              `}
            >
              발송
            </th>
          </tr>
          <tr
            css={css`
              th {
                top: 40px;
                height: 30px;
                font-weight: 500;
              }
            `}
          >
            <th style={pinHead(left.flag, W.flag, 40, { padding: 0 })} />
            {selectable && <th style={pinHead(left.chk, W.chk, 40)} />}
            <th style={pinHead(left.name, W.name, 40)} />
            <th
              style={pinHead(left.owner, W.owner, 40, {
                borderRight: `1px solid ${C.rule}`
              })}
            />
            {OTHER_TAX_TYPES.map((type, i) =>
              sortableTh(
                `notice:${type}`,
                OTHER_TAX_TYPE_LABELS[type],
                i === 0 ? `border-left:1px solid ${C.grid};` : undefined
              )
            )}
            <th
              css={css`
                border-left: 1px solid ${C.grid};
                min-width: ${PHONE_W}px;
              `}
            >
              수신 번호
            </th>
            {OTHER_TAX_TYPES.map((type) =>
              sortableTh(`sent:${type}`, OTHER_TAX_TYPE_LABELS[type])
            )}
          </tr>
        </thead>
        <tbody>
          {rows.length ? (
            rows.map((row, index) => (
              <GridRow
                key={row.businessId}
                row={row}
                index={index}
                selectable={selectable}
                highlightType={highlightType}
                left={left}
                isSelected={selected.has(row.businessId)}
                onToggleSelected={onToggleSelected}
                onDragStart={onDragStart}
                onDragEnter={onDragEnter}
                onNoticeCellClick={onNoticeCellClick}
                onPhoneClick={onPhoneClick}
                onDispatchClick={onDispatchClick}
              />
            ))
          ) : (
            <tr>
              <td
                colSpan={20}
                css={css`
                  text-align: center;
                  padding: 60px 0;
                  color: ${PALETTE.textMid};
                  font-size: 14px;
                `}
              >
                {loading ? "데이터를 확인하는 중입니다." : "데이터가 없습니다."}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default OtherTaxGrid;
